// ================================================================
// SetupWizard.tsx — 首次使用設定精靈
// 讓管理者在瀏覽器自行輸入 Entra App Registration 資訊
// 完成後儲存至 localStorage，頁面重新載入進入登入流程
// ================================================================

import { useState, CSSProperties } from 'react';
import { saveConfig, defaultRedirectUri, PlatformConfig } from '../config';
import { C, btn } from '../styles';

// ── Step indicator ────────────────────────────────────────────
function StepDot({ n, active, done }: { n: number; active: boolean; done: boolean }) {
  const bg = done ? C.green : active ? C.blue : C.bg3;
  const color = done || active ? '#fff' : C.t4;
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%',
      background: bg, color, fontWeight: 700, fontSize: 13,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, transition: 'background .2s',
    }}>
      {done ? '✓' : n}
    </div>
  );
}

// ── Input field ───────────────────────────────────────────────
function Field({ label, hint, value, onChange, placeholder, mono = true }: {
  label: string; hint?: string; value: string;
  onChange: (v: string) => void; placeholder?: string; mono?: boolean;
}) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{ display: 'block', color: C.t2, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
        {label}
      </label>
      {hint && <div style={{ color: C.t4, fontSize: 12, marginBottom: 6, lineHeight: 1.5 }}>{hint}</div>}
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', background: C.bg2, border: `1px solid ${C.border}`,
          borderRadius: 8, color: C.t1, padding: '10px 14px', fontSize: 13,
          fontFamily: mono ? 'monospace' : 'inherit', outline: 'none',
          transition: 'border-color .15s',
        }}
        onFocus={e => (e.target.style.borderColor = C.blue)}
        onBlur={e => (e.target.style.borderColor = C.border)}
      />
    </div>
  );
}

// ── Main Wizard ───────────────────────────────────────────────
export default function SetupWizard() {
  const [step, setStep] = useState(1);
  const [clientId, setClientId]   = useState('');
  const [tenantId, setTenantId]   = useState('common');
  const [redirect, setRedirect]   = useState(defaultRedirectUri());
  const [saving, setSaving]        = useState(false);
  const [error, setError]          = useState('');

  const steps = [
    { n: 1, label: '建立 App Registration' },
    { n: 2, label: '輸入設定值' },
    { n: 3, label: '完成' },
  ];

  const handleSave = async () => {
    setError('');
    if (!clientId.trim()) { setError('請輸入 Application (client) ID'); return; }

    const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!guidPattern.test(clientId.trim())) {
      setError('Client ID 格式不正確，應為 xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx');
      return;
    }

    setSaving(true);
    const cfg: PlatformConfig = {
      loginClientId: clientId.trim(),
      tenantId:      tenantId.trim() || 'common',
      redirectUri:   redirect.trim() || defaultRedirectUri(),
    };
    saveConfig(cfg);
    setStep(3);
    setSaving(false);
  };

  return (
    <div style={{
      minHeight: '100vh', background: C.bg0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        background: C.bg1, border: `1px solid ${C.border}`,
        borderRadius: 16, width: '100%', maxWidth: 580,
        boxShadow: '0 8px 48px rgba(0,0,0,.4)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '28px 32px 20px',
          borderBottom: `1px solid ${C.border}`,
          background: C.bg2,
        }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🛡️</div>
          <h1 style={{ color: C.t1, fontSize: 20, fontWeight: 700, margin: 0 }}>
            AI Governance Platform 初始設定
          </h1>
          <p style={{ color: C.t3, fontSize: 13, margin: '6px 0 0' }}>
            首次使用需要設定 Microsoft Entra App Registration
          </p>
        </div>

        {/* Step indicator */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 0,
          padding: '16px 32px', borderBottom: `1px solid ${C.border}`,
        }}>
          {steps.map((s, i) => (
            <div key={s.n} style={{ display: 'flex', alignItems: 'center', flex: i < steps.length - 1 ? 1 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <StepDot n={s.n} active={step === s.n} done={step > s.n} />
                <span style={{
                  color: step === s.n ? C.t1 : step > s.n ? C.green : C.t4,
                  fontSize: 12, fontWeight: step === s.n ? 600 : 400,
                }}>
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div style={{
                  flex: 1, height: 1, background: step > s.n ? C.green : C.border,
                  margin: '0 12px', transition: 'background .3s',
                }} />
              )}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div style={{ padding: '28px 32px' }}>

          {/* Step 1: Guide to create App Reg */}
          {step === 1 && (
            <div>
              <h2 style={{ color: C.t1, fontSize: 16, fontWeight: 600, marginTop: 0 }}>
                在 Microsoft Entra 建立 App Registration
              </h2>
              <p style={{ color: C.t3, fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
                請以 Global Administrator 帳號登入 Entra，依以下步驟建立一個「登入用」App Registration：
              </p>

              {[
                { n: 1, text: '前往 https://entra.microsoft.com → App registrations → New registration' },
                { n: 2, text: 'Name 填入：ai-platform-login（任意名稱皆可）' },
                { n: 3, text: 'Supported account types 選：Accounts in any organizational directory (Multitenant)' },
                { n: 4, text: `Redirect URI 選 Single-page application (SPA)，填入：${defaultRedirectUri()}` },
                { n: 5, text: '點 Register，記下 Application (client) ID 與 Directory (tenant) ID' },
                { n: 6, text: 'Expose an API → Add a scope → 接受預設 URI → 新增 scope：access_as_user' },
              ].map(item => (
                <div key={item.n} style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                    background: C.blue + '22', border: `1px solid ${C.blue}44`,
                    color: C.blue, fontWeight: 700, fontSize: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginTop: 1,
                  }}>{item.n}</div>
                  <span style={{ color: C.t2, fontSize: 13, lineHeight: 1.6 }}>{item.text}</span>
                </div>
              ))}

              <div style={{
                background: C.blue + '11', border: `1px solid ${C.blue}33`,
                borderRadius: 8, padding: '12px 16px', marginTop: 8, marginBottom: 24,
              }}>
                <span style={{ color: C.blue, fontSize: 12 }}>
                  ℹ️ 此 App Registration 僅用於使用者登入，不需要任何 Graph API 權限。
                  Copilot 資料擷取使用另一個獨立的 App Registration（已在 Phase 1 設定）。
                </span>
              </div>

              <button
                onClick={() => setStep(2)}
                style={{ ...btn('primary') as CSSProperties, fontSize: 14, padding: '11px 24px' }}
              >
                已完成 → 下一步
              </button>
            </div>
          )}

          {/* Step 2: Input config values */}
          {step === 2 && (
            <div>
              <h2 style={{ color: C.t1, fontSize: 16, fontWeight: 600, marginTop: 0 }}>
                輸入 App Registration 資訊
              </h2>

              <Field
                label="Application (client) ID *"
                hint="在 App Registration 概覽頁面右上方，格式：xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={clientId}
                onChange={setClientId}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              />

              <Field
                label="Directory (tenant) ID"
                hint="填 common 代表允許任何 Microsoft 365 組織登入（推薦）；或填入特定 tenant GUID 限制單一組織"
                value={tenantId}
                onChange={setTenantId}
                placeholder="common"
              />

              <Field
                label="Redirect URI"
                hint="必須與 App Registration 中設定的 Redirect URI 完全一致"
                value={redirect}
                onChange={setRedirect}
                placeholder={defaultRedirectUri()}
              />

              {error && (
                <div style={{
                  background: C.red + '11', border: `1px solid ${C.red}33`,
                  borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                  color: C.red, fontSize: 13,
                }}>
                  ⚠️ {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button
                  onClick={() => setStep(1)}
                  style={{ ...btn('ghost') as CSSProperties, fontSize: 13 }}
                >
                  ← 上一步
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{ ...btn('primary') as CSSProperties, fontSize: 14, padding: '11px 24px' }}
                >
                  {saving ? '儲存中…' : '儲存並繼續 →'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Done */}
          {step === 3 && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 52, marginBottom: 16 }}>✅</div>
              <h2 style={{ color: C.green, fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                設定完成！
              </h2>
              <p style={{ color: C.t3, fontSize: 13, lineHeight: 1.7, marginBottom: 28 }}>
                Entra App Registration 資訊已儲存。<br />
                點擊下方按鈕進入登入頁面。
              </p>
              <button
                onClick={() => window.location.reload()}
                style={{ ...btn('primary') as CSSProperties, fontSize: 15, padding: '12px 32px' }}
              >
                🔐 前往登入
              </button>
              <p style={{ color: C.t4, fontSize: 11, marginTop: 20 }}>
                如需修改設定，可在登入後至 設定 → Entra 設定 更改
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
